/**
 * Category:
 * Overview - OVERVIEW
 * Mainland Opportunities - MAINLAND_OPPORTUNITY
 * Manpower Development - MANPOWER
 * Metrology, Accreditation and Standards - METROLOGY
 * Support to T&C Industry - TC_SUPPORT
 * Management System Certification - MANAGEMENT_SYSTEM
 * Chinese Medicines - CHINESE_MEDICINE
 * Construction Materials - CONSTRUCTION
 * Electrical & Electronic Products - ELECTRICAL_PRODUCTS
 * Environmental Protection - ENVIRONMENT_PROTECTION
 * Food - FOOD
 * Information and Communications Technologies - INFORMATION_TECHNOLOGY
 * Jewellery - JEWELLERY
 * Medical testing - MEDICAL_TESTING
 * Textile, Clothing & Footwear - TEXTILE_CLOTHING
 * Toys & Children's Products - TOYS
 *
 * type:
 * PDF - PDF
 * Video - VIDEO
 * */

import dayjs from "dayjs";
import advertorials_en from "./advertorials-en.json";
import advertorials_tc from "./advertorials-tc.json";
import advertorials_sc from "./advertorials-sc.json";
import {
  ADVERTORIALS_SECTOR,
  CATEGORIES,
  MEDIA_TYPE,
  type PublicationType,
} from "../PublicationList_left";
import { Language } from "../../context";

/**
 * Processes English advertorial JSON data into a standardized list of PublicationType objects.
 * Each entry will retain the raw ISO date string in the title, and the yearRange will be derived from that date.
 *
 * @param jsonData - The raw JSON data parsed from the English JSON file.
 * @returns A list of PublicationType entries for English language content.
 *
 * */

const processAdvertorialsJson = (jsonData: any[]): PublicationType[] => {
  return jsonData?.map((item) => {
    const date = dayjs(item?.date) ?? "";
    const year = date?.year();

    return {
      title: item?.date ?? "",
      date: "",
      link: item?.url ?? "",
      mediaType: item?.type as MEDIA_TYPE,
      sector: item?.cate as ADVERTORIALS_SECTOR[],
      category: [CATEGORIES.ADVERTORIALS],
      yearRange: [year, year],
    };
  });
};

/**
 * Processes Traditional or Simplified Chinese advertorial JSON data into PublicationType objects.
 * The title will be formatted as a Chinese date string (e.g., "2023年4月20日").
 * @param jsonData - The raw JSON data parsed from the Chinese JSON file.
 * @returns A list of PublicationType entries with Chinese-formatted titles.
 *
 * */

const processAdvertorialsJson_cn = (jsonData: any[]): PublicationType[] => {
  return jsonData?.map((item) => {
    const date = dayjs(item?.date) ?? "";
    const year = date?.year();

    return {
      title: dayjs(date).format("YYYY年M月D日"),
      date: "",
      link: item?.url ?? "",
      mediaType: item?.type as MEDIA_TYPE,
      sector: item?.cate as ADVERTORIALS_SECTOR[],
      category: [CATEGORIES.ADVERTORIALS],
      yearRange: [year, year],
    };
  });
};

const advertorial_list_en: PublicationType[] =
  processAdvertorialsJson(advertorials_en);
const advertorial_list_tc: PublicationType[] =
  processAdvertorialsJson_cn(advertorials_tc);
const advertorial_list_sc: PublicationType[] =
  processAdvertorialsJson_cn(advertorials_sc);

/**
 * A mapping from each supported language to its respective list of advertorial PublicationType entries.
 * */

export const advertorialLanguageMap: Record<Language, PublicationType[]> = {
  [Language.EN]: advertorial_list_en,
  [Language.TC]: advertorial_list_tc,
  [Language.SC]: advertorial_list_sc,
};
